import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { Density } from '@/enums/common'
import { GaugeStyle } from '@/enums/device'
import { UserRole } from '@/enums/user'
import i18n from '@/libs/i18n'
import type { IMe } from '@/types/Device'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'en')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IMe>()

    /*
     * Analogue dials or the digital cluster — a preference, not a setting anybody has
     * to be asked about, so it is remembered per browser exactly like the locale. The
     * default is the cluster: it is what a modern car shows, and it fits more of what
     * the vehicle actually reports on one screen.
     */
    const gaugeStyle = ref(localStorage.getItem('gaugeStyle') || GaugeStyle.Digital)

    /*
     * Compact by default, and applied to <html> so the root font size — which every
     * rem in the console hangs off — follows it. index.html sets the same class before
     * first paint, so nothing jumps.
     */
    const density = ref<string>(localStorage.getItem('density') || Density.Compact)

    const applyDensity = () => document.documentElement.classList.toggle('density-compact', density.value === Density.Compact)

    const setDensity = (value: string) => {
        density.value = value
        localStorage.setItem('density', value)
        applyDensity()
    }

    /*
     * Whether the instruments panel is open beside the map. It is a third of the row,
     * and an operator watching a track wants the other two thirds back — so the choice
     * is theirs and it is remembered, like the gauge style and the locale.
     */
    const instrumentsOpen = ref(localStorage.getItem('instrumentsOpen') !== 'false')

    /*
     * Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
     * never a second source of truth. Two modes only, 'light' | 'dark' — no System
     * option in the UI (vue-spec theme.md). Without `emitAuto` the ref always reads the
     * RESOLVED mode, so a first visit (nothing stored yet) shows whatever the OS
     * prefers. The storageKey matches what Nuxt UI persists, and the inline script in
     * index.html reads that same key before first paint.
     */
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /*
     * Whether the person reading this may change anything.
     *
     * It answers false until /me has come back, which is the right way round: a
     * screen that draws an edit button the moment it mounts and takes it away a
     * moment later is worse than one that waits. The API enforces the same rule
     * itself on every write — this only keeps a reader from being offered a button
     * that would come back 403.
     */
    const isAdmin = () => userData.value?.role === UserRole.Admin

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor (and
    // the mobile drawer) until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value      = ''
        userData.value = undefined
    }

    const setUserData = (data: IMe) => {
        userData.value = data
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as { value: string }).value = newLocale
    }

    const toggleInstruments = (open: boolean) => {
        instrumentsOpen.value = open
        localStorage.setItem('instrumentsOpen', String(open))
    }

    const changeGaugeStyle = (style: string) => {
        gaugeStyle.value = style
        localStorage.setItem('gaugeStyle', style)
    }

    return {
        JWT, locale, userData, loaderActive, windowWidth, colorMode, gaugeStyle, instrumentsOpen, density,
        getJWT, isLoggedIn, isLoaderActive, isMobile, isAdmin,
        setLogin, setLogout, setUserData, startLoading, stopLoading, changeLocale, changeGaugeStyle, toggleInstruments, setDensity,
    }
})
